import { IChartConfig } from '../types';
import { SingleSeriesChartTypesOptions, MultiSeriesChartTypesOptions } from '../chartConsts';
import { isRTL } from '../../../Localization';

/**
 * Chart data format for the AI analysis API
 */
export interface AIChartData {
  language: string;
  analysisType: string;
  baseFormName: string;
  chartType: string;
  xLabel?: string;
  yLabel?: string;
  x?: string;
  y?: string;
  z?: string;
  series: {
    name: string | null;
    data: Array<{
      x: string | number;
      y: number;
      z?: number;
    }>;
  }[];
}

/**
 * Map chart types from internal representation to API format
 */
const chartTypeMapping: Record<string, string> = {
  // Single series chart types
  [SingleSeriesChartTypesOptions.column2d]: 'column',
  [SingleSeriesChartTypesOptions.bar2d]: 'horizontal_column',
  [SingleSeriesChartTypesOptions.line]: 'line',
  [SingleSeriesChartTypesOptions.pie2d]: 'pie',
  [SingleSeriesChartTypesOptions.doughnut2d]: 'doughnut',
  [SingleSeriesChartTypesOptions.bubble]: 'scatter',
  [SingleSeriesChartTypesOptions.funnel]: 'pyramid',
  
  // Multi series chart types
  [MultiSeriesChartTypesOptions.mscolumn2d]: 'multi_column',
  [MultiSeriesChartTypesOptions.msbar2d]: 'multi_horizontal_column',
  [MultiSeriesChartTypesOptions.msline]: 'multi_line',
  [MultiSeriesChartTypesOptions.stackedcolumn2d]: 'multi_stacked_column',
  [MultiSeriesChartTypesOptions.stackedbar2d]: 'multi_horizontal_stacked_column'
};

/**
 * Serialize chart data from internal format to the format required by the AI analysis API
 * 
 * @param config The chart configuration
 * @param data The chart data
 * @param analysisType The type of analysis to perform
 * @returns Formatted data for the AI analysis API
 */
export function serializeChartData(
  config: IChartConfig,
  data: any,
  analysisType: string
): AIChartData {
  // Get the chart type from the mapping or use a default
  const chartType = chartTypeMapping[config.type] || 'column';
  
  // We'll let the component handle the logging
  
  // Create the base structure
  const result: AIChartData = {
    language: isRTL() ? 'he' : 'en', // Use Hebrew for RTL mode
    analysisType,
    baseFormName: typeof config.baseForm === 'string' ? config.baseForm : 'ChartData',
    chartType,
    series: []
  };
  
  // Add axis labels based on chart configuration
  if (config.xAxis?.Title) {
    if (chartType === 'column' || chartType === 'line') {
      result.xLabel = config.xAxis.Title;
    } else {
      result.x = config.xAxis.Title;
    }
  }
  
  if (config.yAxis?.Title) {
    if (chartType === 'column' || chartType === 'line') {
      result.yLabel = config.yAxis.Title;
    } else {
      result.y = config.yAxis.Title;
    }
  }
  
  // For bubble charts, add z-axis label if available
  if (chartType === 'scatter' && config.bubbleConfig?.zBubbleSize?.Title) {
    result.z = config.bubbleConfig.zBubbleSize.Title;
  }
  
  try {
    // Process the data based on chart type
    if (chartType.startsWith('multi_')) {
      // Handle multi-series charts
      processMultiSeriesData(result, data);
    } else {
      // Handle single-series charts
      processSingleSeriesData(result, data);
    }
    
    return result;
  } catch (error) {
    console.error('Error serializing chart data:', error);
    
    // Return a minimal valid structure if serialization fails
    return {
      language: isRTL() ? 'he' : 'en', // Use Hebrew for RTL mode
      analysisType,
      baseFormName: 'ChartData',
      chartType,
      series: [{
        name: null,
        data: []
      }]
    };
  }
}

/**
 * Process data for single-series charts
 */
function processSingleSeriesData(result: AIChartData, data: any): void {
  // Create a single series
  const series = {
    name: null,
    data: [] as Array<{ x: string | number; y: number; z?: number }>
  };
  
  // Extract data points
  if (data && Array.isArray(data)) {
    // Handle array format
    data.forEach(item => {
      if (item.label && item.value !== undefined) {
        series.data.push({
          x: item.label,
          y: parseFloat(item.value) || 0
        });
      }
    });
  } else if (data && typeof data === 'object') {
    // Handle object format with dataset
    if (data.dataset && Array.isArray(data.dataset)) {
      data.dataset.forEach((dataset: any) => {
        if (dataset.data && Array.isArray(dataset.data)) {
          dataset.data.forEach((point: any) => {
            if (point.x !== undefined && point.y !== undefined) {
              series.data.push({
                x: point.x,
                y: parseFloat(point.y) || 0,
                ...(point.z !== undefined && { z: parseFloat(point.z) || 0 })
              });
            } else if (point.label && point.value !== undefined) {
              series.data.push({
                x: point.label,
                y: parseFloat(point.value) || 0
              });
            }
          });
        }
      });
    } else if (data.data && Array.isArray(data.data)) {
      // Handle direct data array
      data.data.forEach((point: any) => {
        if (point.label && point.value !== undefined) {
          series.data.push({
            x: point.label,
            y: parseFloat(point.value) || 0
          });
        }
      });
    }
  }
  
  // Add the series to the result
  result.series.push(series);
}

/**
 * Process data for multi-series charts
 */
function processMultiSeriesData(result: AIChartData, data: any): void {
  // Create a map to organize data by series name
  const seriesMap = new Map<string, Array<{ x: string | number; y: number }>>();
  
  // Extract categories (x-axis values)
  const categories: string[] = [];
  if (data && data.categories && Array.isArray(data.categories)) {
    data.categories.forEach((category: any) => {
      if (category.category && Array.isArray(category.category)) {
        category.category.forEach((cat: any) => {
          if (cat.label) {
            categories.push(cat.label);
          }
        });
      }
    });
  }
  
  // Extract series data
  if (data && data.dataset && Array.isArray(data.dataset)) {
    data.dataset.forEach((dataset: any) => {
      const seriesName = dataset.seriesname || null;
      const seriesData: Array<{ x: string | number; y: number }> = [];
      
      if (dataset.data && Array.isArray(dataset.data)) {
        dataset.data.forEach((point: any, index: number) => {
          const xValue = categories[index] || `Category ${index + 1}`;
          seriesData.push({
            x: xValue,
            y: parseFloat(point.value) || 0
          });
        });
      }
      
      seriesMap.set(seriesName || 'Series', seriesData);
    });
  }
  
  // Convert the map to the required format
  seriesMap.forEach((data, name) => {
    result.series.push({
      name,
      data
    });
  });
  
  // If no series were found, add an empty one
  if (result.series.length === 0) {
    result.series.push({
      name: null,
      data: []
    });
  }
}
